import React from "react";
import { Cigarette } from "lucide-react";

/**
 * SmokingFeeToggle — customer-facing accept/decline for the $200
 * in-vehicle smoking upcharge. Applies to both chauffeur and rental
 * bookings. Declining is the default; the option must be actively
 * accepted before the fee rolls into the estimate.
 *
 * Why is this its own component (and not a checkbox)?
 * Because it needs to be visually LOUD — smoking without paying
 * this fee voids the deposit under contract, so we want the
 * customer to see the number and decide deliberately, not skim past
 * it as another tiny checkbox.
 */
export default function SmokingFeeToggle({ accepted, onChange, fee = 200, testId = "smoking-fee-toggle" }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl border p-4 space-y-3 transition ${
        accepted
          ? "border-amber-400/50 bg-amber-400/[0.06]"
          : "border-white/10 bg-white/[0.02]"
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
          accepted ? "bg-amber-400/20 text-amber-300" : "bg-white/5 text-white/60"
        }`}>
          <Cigarette size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-white">Smoking permitted in vehicle</span>
            <span className="label-caps text-[10px] px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-300 border border-amber-400/30">
              +${Number(fee).toFixed(2)}
            </span>
          </div>
          <p className="text-[11px] text-white/60 mt-1 leading-relaxed">
            One-time upcharge. If you don't accept and we detect smoke in the vehicle, your entire deposit is forfeit under the rental agreement.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          data-testid={`${testId}-decline`}
          onClick={() => onChange(false)}
          className={`py-2.5 rounded-full text-[11px] label-caps border transition ${
            !accepted
              ? "bg-white/8 border-white/20 text-white"
              : "border-white/10 text-white/50 hover:text-white/80"
          }`}
        >
          No smoking · decline
        </button>
        <button
          type="button"
          data-testid={`${testId}-accept`}
          onClick={() => onChange(true)}
          className={`py-2.5 rounded-full text-[11px] label-caps border transition ${
            accepted
              ? "bg-amber-400/20 border-amber-400 text-amber-200"
              : "border-white/10 text-white/50 hover:text-white/80"
          }`}
        >
          Accept · +${Number(fee).toFixed(2)}
        </button>
      </div>
    </div>
  );
}
